import axios from 'axios';

const client = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 防止 401 重复跳转
let _isRedirecting = false;

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && !_isRedirecting) {
      _isRedirecting = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.dispatchEvent(new CustomEvent('auth-expired'));
        window.location.href = '/login';
      }
      // 5秒后重置标记，避免永久锁定
      setTimeout(() => { _isRedirecting = false; }, 5000);
    }
    return Promise.reject(error);
  }
);

const authAPI = {
  login: (data) => client.post('/auth/login', data),
  getMe: () => client.get('/auth/me'),
  updateMe: (data) => client.put('/auth/me', data),
  register: (data) => client.post('/auth/register', data),
  getUsers: (params) => client.get('/users/', { params }),
};

const productAPI = {
  list: (params) => client.get('/products/', { params }),
  create: (data) => client.post('/products/', data),
  get: (id) => client.get(`/products/${id}`),
  update: (id, data) => client.put(`/products/${id}`, data),
  remove: (id) => client.delete(`/products/${id}`),
  confirm: (id) => client.post(`/products/${id}/confirm`),
  createPending: (data) => client.post('/products/create-pending', data),
  getCategories: () => client.get('/products/categories/list/'),
  getColors: () => client.get('/products/colors/list/'),
  getSizes: () => client.get('/products/sizes/list/'),
  getColorsByName: (name) => client.get('/products/colors/by-name/', { params: { name } }),
  downloadTemplate: () => client.get('/products/template/download/', { responseType: 'blob' }),
  batchImport: (formData) => client.post('/products/batch-import/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

const supplierAPI = {
  list: (params) => client.get('/suppliers/', { params }),
  create: (data) => client.post('/suppliers/', data),
  get: (id) => client.get(`/suppliers/${id}`),
  update: (id, data) => client.put(`/suppliers/${id}`, data),
  remove: (id) => client.delete(`/suppliers/${id}`),
};

const purchaseAPI = {
  list: (params) => client.get('/purchase-orders/', { params }),
  getItems: (poId) => client.get(`/purchase-orders/${poId}/items/`),
  create: (data) => client.post('/purchase-orders/', data),
  upload: (formData) =>
    client.post('/purchase-orders/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    }),
  uploadFolder: (formData, onProgress) =>
    client.post('/purchase-orders/upload-folder/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: onProgress,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }),
  batchCreate: (data) => client.post('/purchase-orders/batch-create/', data),
  get: (id) => client.get(`/purchase-orders/${id}`),
  update: (id, data) => client.put(`/purchase-orders/${id}`, data),
  delete: (id) => client.delete(`/purchase-orders/${id}/`, { timeout: 60000 }),
  updateItemStatus: (purchaseId, itemId, status) =>
    client.put(`/purchase-orders/${purchaseId}/items/${itemId}/status`, { status }),
  setPriority: (purchaseId, priority) =>
    client.put(`/purchase-orders/${purchaseId}/priority`, null, {
      params: { priority },
    }),
  getBySupplier: (supplierId, params) =>
    client.get(`/purchase-orders/supplier/${supplierId}/`, { params }),
  searchItem: (keyword) =>
    client.get('/purchase-orders/search-item', { params: { keyword } }),
  updateItemPrice: (purchaseId, itemId, unit_price) =>
    client.put(`/purchase-orders/${purchaseId}/items/${itemId}/price`, { unit_price }),
  batchUpdatePrice: (purchaseId, items) =>
    client.put(`/purchase-orders/${purchaseId}/items/batch-price`, { items }),
  confirm: (id) => client.put(`/purchase-orders/${id}/confirm/`),
  syncToOrders: (id, data) => client.post(`/purchase-orders/${id}/sync-to-orders/`, data),
  leftovers: () => client.get('/purchase-orders/leftovers/'),
  mergeShippingPdfs: (orderNos, orderIds) => client.post('/purchase-orders/merge-shipping-pdfs/',
    { order_nos: orderNos, ...(orderIds ? { order_ids: orderIds } : {}) },
    { responseType: 'blob', timeout: 300000 }),
  stampGroupPdf: (orderIds) => client.post('/purchase-orders/stamp-group-pdf/',
    { order_ids: orderIds }, { responseType: 'blob' }),
  batchShip: (trackingNumbers) => client.post('/customer-orders/batch-ship/', { tracking_numbers: trackingNumbers }),
  batchCancel: (trackingNumbers) => client.post('/customer-orders/batch-cancel/', { tracking_numbers: trackingNumbers }),
  quote: (id) => client.put(`/purchase-orders/${id}/quote/`),
  exportPrice: (id, opts) => client.get(`/purchase-orders/${id}/export-price/`, { responseType: 'blob', ...opts }),
  importPrice: (poId, formData) => client.post(`/purchase-orders/${poId}/import-price/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportFull: (id) => client.get(`/purchase-orders/${id}/export-full/`, { responseType: 'blob' }),
  downloadProductionPackage: (poId) => client.get(`/purchase-orders/${poId}/download-production-package/`, { responseType: 'blob', timeout: 300000 }),
  productionPackageStats: (poId) => client.get(`/purchase-orders/${poId}/production-package-stats/`),
  startProductionPackage: (poId, data) => client.post(`/purchase-orders/${poId}/start-production-package/`, data || {}),
  getPackageProgress: (taskId) => client.get(`/purchase-orders/production-package-progress/${taskId}`),
  downloadPackage: (taskId, opts = {}) => client.get(`/purchase-orders/download-package/${taskId}`, { responseType: 'blob', timeout: 1800000, ...opts }),
  cancelPackageDownload: (taskId) => client.post(`/purchase-orders/download-package/${taskId}/cancel`),
  releasePackage: (taskId) => client.delete(`/purchase-orders/download-package/${taskId}`),
};

const purchaseConfirmAPI = {
  // ── 双确认流程 ──
  supplierConfirm: (itemId, data) => client.put(`/purchase-orders/items/${itemId}/supplier-confirm/`, data),
  customerConfirm: (itemId, data) => client.put(`/purchase-orders/items/${itemId}/customer-confirm/`, data),
  replaceImage: (itemId, data) => client.put(`/purchase-orders/items/${itemId}/replace-image/`, data),
  uploadItemImage: (itemId, formData) => client.post(`/purchase-orders/items/${itemId}/upload-image/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  batchHold: (poId, data) => client.post(`/purchase-orders/${poId}/batch-hold/`, data),
  syncItems: (poId, data) => client.post(`/purchase-orders/${poId}/sync-items/`, data),
  pendingSummary: () => client.get('/purchase-orders/pending-summary/'),
  markViewed: (itemId) => client.put(`/purchase-orders/items/${itemId}/mark-viewed/`),
  getItemComments: (itemId) => client.get(`/purchase-orders/items/${itemId}/comments/`),
  addItemComment: (itemId, data) => client.post(`/purchase-orders/items/${itemId}/comments/`, data),
  downloadItemImages: (itemId) => client.get(`/purchase-orders/items/${itemId}/download-images/`, { responseType: 'blob' }),
  downloadAllImages: (poId) => client.get(`/purchase-orders/${poId}/download-all-images/`, { responseType: 'blob' }),
  checkInventory: (poId) => client.get(`/purchase-orders/${poId}/inventory-check`),
  replaceImageV2: (itemId, field, file) => {
    const formData = new FormData();
    formData.append('field', field);
    formData.append('file', file);
    return client.put(`/purchase-orders/items/${itemId}/replace-image-v2/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeImage: (itemId, field) => {
    const formData = new FormData();
    formData.append('field', field);
    return client.put(`/purchase-orders/items/${itemId}/remove-image/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getImageHistory: (itemId) => client.get(`/purchase-orders/items/${itemId}/image-history/`),
  updateTexts: (itemId, data) => client.put(`/purchase-orders/items/${itemId}/update-texts/`, data),
  getTextHistory: (itemId) => client.get(`/purchase-orders/items/${itemId}/text-history/`),
  updateNote: (itemId, note) => client.put(`/purchase-orders/items/${itemId}/update-note/`, { note }),
};

const inventoryAPI = {
  list: (params) => client.get('/inventory/', { params }),
  get: (productId) => client.get(`/inventory/${productId}`),
  getByProduct: (productId) => client.get(`/inventory/by-product/${productId}/`),
  adjust: (productId, data, reason, color) =>
    client.put(`/inventory/adjust/`, data, {
      params: { reason, product_id: productId, color: color || null },
    }),
  getWarnings: () => client.get('/inventory/warnings/list'),
  setMinStock: (productId, minStock, color) =>
    client.put(`/inventory/set-min-stock/`, null, {
      params: { product_id: productId, min_stock: minStock, color: color || null },
    }),
  getSalesStats: (params) => client.get('/inventory/sales-stats/', { params }),
  uploadProductImage: (formData) => client.post('/inventory/upload-product-image/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteInventory: (id) => client.delete(`/inventory/${id}/`),
  toggleStatus: (id) => client.put(`/inventory/${id}/toggle-status/`),
};

const outboundAPI = {
  list: (params) => client.get('/outbound/', { params }),
  create: (data) => client.post('/outbound/', data),
  scan: (data) =>
    client.post('/outbound/scan', null, { params: data }),
  get: (id) => client.get(`/outbound/${id}`),
  updateStatus: (id, status) =>
    client.put(`/outbound/${id}/status`, { status }),
  getByTracking: (trackingNo) => client.get(`/outbound/tracking/${trackingNo}`),
};

const reportAPI = {
  inventorySummary: (params) => client.get('/reports/inventory-summary', { params }),
  purchaseSummary: (params) => client.get('/reports/purchase-summary', { params }),
  outboundSummary: (params) => client.get('/reports/outbound-summary', { params }),
  outboundDaily: (params) => client.get('/reports/outbound-daily', { params }),
  supplierPerformance: (params) => client.get('/reports/supplier-performance', { params }),
  lowStock: (params) => client.get('/reports/low-stock', { params }),
  inventoryTrend: (params) => client.get('/reports/inventory-trend', { params }),
};

const customerOrderAPI = {
  list: (params) => client.get('/customer-orders/', { params }),
  stats: () => client.get('/customer-orders/stats/'),
  create: (data) => client.post('/customer-orders/', data),
  get: (id) => client.get(`/customer-orders/${id}`),
  update: (id, data) => client.put(`/customer-orders/${id}`, data),
  updateProductionStatus: (id, production_status) =>
    client.put(`/customer-orders/${id}/production-status`, { production_status }),
  updateShippingStatus: (id, shipping_status, tracking_number, produced) =>
    client.put(`/customer-orders/${id}/shipping-status`, { shipping_status, tracking_number, produced }),
  uploadImage: (formData) =>
    client.post('/customer-orders/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadPdf: (formData) =>
    client.post('/customer-orders/upload-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadFolder: (formData, onProgress) =>
    client.post('/customer-orders/upload-folder/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: onProgress,
    }),
  batchCreate: (data) => client.post('/customer-orders/batch-create/', data),
  reorder: (id) => client.post(`/customer-orders/${id}/reorder/`),
  batchReorder: (data) => client.post('/customer-orders/batch-reorder/', data),
  delete: (id) => client.delete(`/customer-orders/${id}`),
  batchMark: (data) => client.put('/customer-orders/batch-mark/', data),
  scanShip: (data) => client.post('/customer-orders/scan-ship/', data),
  scrap: (id, data) => client.post(`/customer-orders/${id}/scrap/`, data),
  printWorksheet: (data) => client.post('/customer-orders/print-worksheet/', data),
  batchUpdateProductionStatus: (ids, production_status) =>
    client.put('/customer-orders/batch-production-status/', { order_ids: ids, production_status }),
  forceStatus: (id, data) => client.put(`/customer-orders/${id}/force-status/`, data),
  scanLookup: (tracking_number) => client.get('/customer-orders/', { params: { search: tracking_number, page_size: 50 } }),
  shippingOverview: () => client.get('/customer-orders/shipping-overview/'),
};

const notificationAPI = {
  list: (params) => client.get('/notifications/', { params }),
  unreadCount: () => client.get('/notifications/unread-count/'),
  markRead: (id) => client.put(`/notifications/${id}/read/`),
  markAcknowledge: (id) => client.put(`/notifications/${id}/acknowledge/`),
  markResolve: (id) => client.put(`/notifications/${id}/resolve/`),
  reply: (id, data) => client.post(`/notifications/${id}/reply/`, data),
  getReplies: (id) => client.get(`/notifications/${id}/replies/`),
  send: (data) => client.post('/notifications/send/', data),
};

const pendingInboundAPI = {
  list: (params) => client.get('/pending-inbound/', { params }),
  create: (data) => client.post('/pending-inbound/', data),
  batchCreate: (data) => client.post('/pending-inbound/batch/', data),
  update: (id, data) => client.put(`/pending-inbound/${id}/`, data),
  receive: (id, data) => client.post(`/pending-inbound/${id}/receive/`, data),
  confirm: (id) => client.post(`/pending-inbound/${id}/confirm/`),
  batchConfirm: (ids) => client.post('/pending-inbound/batch-confirm/', ids),
  remove: (id) => client.delete(`/pending-inbound/${id}/`),
};

const categoryAPI = {
  list: () => client.get('/categories/'),
  create: (data) => client.post('/categories/', data),
  update: (id, data) => client.put(`/categories/${id}/`, data),
  remove: (id) => client.delete(`/categories/${id}/`),
};

const scrapAPI = {
  list: (params) => client.get('/scraps/', { params }),
  create: (data) => client.post('/scraps/', data),
  remove: (id) => client.delete(`/scraps/${id}/`),
  summary: (params) => client.get('/scraps/summary/', { params }),
  reasons: () => client.get('/scraps/reasons/'),
  stats: (params) => client.get('/scraps/stats/', { params }),
};

const settingAPI = {
  get: (key) => client.get(`/settings/${key}`),
  set: (key, value) => client.put(`/settings/${key}`, { value }),
};

export { authAPI, productAPI, supplierAPI, purchaseAPI, purchaseConfirmAPI, inventoryAPI, outboundAPI, reportAPI, customerOrderAPI, notificationAPI, pendingInboundAPI, categoryAPI, scrapAPI, settingAPI };
export default client;
